/**
 * F-37: the support reference for a failed request. worker-api stamps every response with
 * `x-request-id` (Cloudflare's `cf-ray` when present) and `api.ts` carries it on the typed
 * `ApiError`. Screens thread it into `ErrorState`'s `requestId` so a user can quote it to
 * support (design-system.md §13: every error state shows a reference).
 *
 * Kept next to — not inside — `getErrorMessage` so `api.ts` stays a pure client binding.
 */
import { getErrorMessage, isApiError } from './api.ts';

/** The request's correlation id, or undefined for transport failures / non-API errors. */
export function getRequestId(err: unknown): string | undefined {
  return isApiError(err) ? err.requestId : undefined;
}

/**
 * F-39: what a string-fed error state needs to keep — the safe message AND the reference.
 * Components that hold a caught error in `useState` (rather than reading a query/mutation's
 * `.error`) store this so the "Reference: <id>" line still renders. The `requestId` key is
 * OMITTED (not set to undefined) when absent, so the shape spreads cleanly under
 * `exactOptionalPropertyTypes`.
 */
export interface DescribedError {
  message: string;
  requestId?: string;
}

export function describeError(err: unknown): DescribedError {
  const message = getErrorMessage(err);
  const requestId = getRequestId(err);
  return requestId ? { message, requestId } : { message };
}
